<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12">
                <div class="card shadow-lg border-0">
                    <div class="card-header text-white py-3" style="background-color: #3e5fce;">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 class="mb-0"><i class="fa fa-chart-bar me-2"></i>Advance Salary Reports</h4>
                                <small class="opacity-75">Comprehensive reporting and analytics</small>
                            </div>
                            <div class="text-end">
                                <span class="badge bg-white px-3 py-2" style="color: #3e5fce;">
                                    <i class="fa fa-calendar me-1"></i>{{ getCurrentDate() }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="card-body p-4">
                        <!-- Filter Section -->
                        <div class="filter-section p-4 mb-4 bg-white rounded-3 shadow-sm" style="border: 2px solid #3e5fce;">
                            <h6 class="mb-3" style="color: #3e5fce;"><i class="fa fa-filter me-2"></i>Report Filters</h6>
                            <div class="row g-3">
                                <div class="col-md-3">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-file-text-o me-1"></i>Report Type <span class="text-danger">*</span>
                                    </label>
                                    <select v-model="filters.reportType" class="form-select form-select-lg shadow-sm" @change="onReportTypeChange">
                                        <option value="">Select Report Type</option>
                                        <option value="manager">👔 Manager-wise</option>
                                        <option value="site">🏢 Site-wise</option>
                                        <option value="employee">👤 Employee-wise</option>
                                    </select>
                                </div>
                                
                                <div class="col-md-3">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-calendar-check-o me-1"></i>Start Date
                                    </label>
                                    <input type="date" v-model="filters.startDate" class="form-control form-control-lg shadow-sm" />
                                </div>
                                
                                <div class="col-md-3">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-calendar-times-o me-1"></i>End Date
                                    </label>
                                    <input type="date" v-model="filters.endDate" class="form-control form-control-lg shadow-sm" />
                                </div>

                                <div class="col-md-3" v-if="filters.reportType === 'manager'">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-user-tie me-1"></i>Manager
                                    </label>
                                    <select v-model="filters.managerId" class="form-select form-select-lg shadow-sm">
                                        <option value="">All Managers</option>
                                        <option v-for="manager in managers" :key="manager._id" :value="manager._id">
                                            {{ manager.employeeName }}
                                        </option>
                                    </select>
                                </div>

                                <div class="col-md-3" v-if="filters.reportType === 'site' || filters.reportType === 'employee'">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-building-o me-1"></i>Site
                                    </label>
                                    <select v-model="filters.siteId" class="form-select form-select-lg shadow-sm">
                                        <option value="">All Sites</option>
                                        <option v-for="site in sites" :key="site._id" :value="site._id">
                                            {{ site.siteName }}
                                        </option>
                                    </select>
                                </div>

                                <div class="col-md-3" v-if="filters.reportType === 'employee'">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-user me-1"></i>Employee
                                    </label>
                                    <select v-model="filters.employeeId" class="form-select form-select-lg shadow-sm">
                                        <option value="">All Employees</option>
                                        <option v-for="employee in employees" :key="employee._id" :value="employee._id">
                                            {{ employee.employeeName }} - {{ employee.employeeCode }}
                                        </option>
                                    </select>
                                </div>

                                <div class="col-md-3">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-info-circle me-1"></i>Status
                                    </label>
                                    <select v-model="filters.status" class="form-select form-select-lg shadow-sm">
                                        <option value="">All Status</option>
                                        <option value="Pending">⏳ Pending</option>
                                        <option value="Approved">✅ Approved</option>
                                        <option value="Rejected">❌ Rejected</option>
                                    </select>
                                </div>

                                <div class="col-md-3">
                                    <label class="form-label fw-semibold">
                                        <i class="fa fa-sitemap me-1"></i>Department
                                    </label>
                                    <select v-model="filters.departmentId" class="form-select form-select-lg shadow-sm">
                                        <option value="">All Departments</option>
                                        <option v-for="dept in departments" :key="dept._id" :value="dept._id">
                                            {{ dept.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div class="action-buttons-section mb-4">
                            <div class="d-flex flex-wrap gap-2">
                                <button @click="fetchReport" class="btn btn-lg px-4 shadow custom-btn-primary" :disabled="loading || !filters.reportType">
                                    <i class="fa fa-search me-2"></i> Generate Report
                                </button>
                                <button @click="exportToExcel" class="btn btn-lg px-4 shadow custom-btn-success" :disabled="!reportData || loading">
                                    <i class="fa fa-file-excel-o me-2"></i> Export to Excel
                                </button>
                                <button @click="resetFilters" class="btn btn-lg px-4 shadow-sm custom-btn-outline">
                                    <i class="fa fa-refresh me-2"></i> Reset Filters
                                </button>
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                            <div class="loading-container">
                                <div class="spinner-border" role="status" style="width: 3rem; height: 3rem; color: #3e5fce;">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p class="mt-3 text-muted fw-semibold">Generating your report...</p>
                            </div>
                        </div>

                        <!-- Report Data -->
                        <div v-else-if="reportData && reportData.data && reportData.data.length > 0">
                            <!-- Manager Report -->
                            <div v-if="reportData.reportType === 'manager'">
                                <div class="report-header mb-4">
                                    <h5 style="color: #3e5fce;"><i class="fa fa-user-tie me-2"></i>Manager-wise Report</h5>
                                    <hr class="divider">
                                </div>
                                <div v-for="(manager, index) in reportData.data" :key="index" class="card mb-4 shadow-sm border-0 report-card">
                                    <div class="card-body p-4">
                                        <div class="d-flex justify-content-between align-items-center mb-3">
                                            <h5 class="card-title text-dark mb-0">
                                                <i class="fa fa-user-circle me-2" style="color: #3e5fce;"></i>{{ manager.managerName }}
                                            </h5>
                                            <button class="btn btn-sm px-3 custom-btn-outline" @click="toggleRequests(index)">
                                                <i :class="showRequests[index] ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                                                {{ showRequests[index] ? 'Hide' : 'Show' }} Details
                                            </button>
                                        </div>
                                        
                                        <div class="row g-3">
                                            <div class="col-md-4">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-file-text-o me-2"></i>Request Statistics</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Requests:</span>
                                                        <span class="stat-value badge bg-primary">{{ manager.totalRequests }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Approved:</span>
                                                        <span class="stat-value badge bg-success">{{ manager.approvedCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Rejected:</span>
                                                        <span class="stat-value badge bg-danger">{{ manager.rejectedCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Pending:</span>
                                                        <span class="stat-value badge bg-warning text-dark">{{ manager.pendingCount }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-money me-2"></i>Amount Details</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Requested:</span>
                                                        <span class="stat-value text-primary fw-bold">₹{{ formatAmount(manager.totalAmountRequested) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Approved:</span>
                                                        <span class="stat-value text-success fw-bold">₹{{ formatAmount(manager.totalAmountApproved) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Rejected:</span>
                                                        <span class="stat-value text-danger fw-bold">₹{{ formatAmount(manager.totalAmountRejected) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-line-chart me-2"></i>Key Metrics</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Critical Requests:</span>
                                                        <span class="stat-value badge bg-warning text-dark">{{ manager.criticalRequestCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Avg Request Amount:</span>
                                                        <span class="stat-value text-info fw-bold">₹{{ formatAmount(manager.averageRequestAmount) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Approval Rate:</span>
                                                        <span class="stat-value">
                                                            <div class="progress" style="height: 20px;">
                                                                <div class="progress-bar bg-success" role="progressbar" 
                                                                     :style="{width: manager.approvalRate + '%'}" 
                                                                     :aria-valuenow="manager.approvalRate" 
                                                                     aria-valuemin="0" aria-valuemax="100">
                                                                    {{ manager.approvalRate }}%
                                                                </div>
                                                            </div>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="showRequests[index]" class="mt-4">
                                            <div class="table-responsive">
                                                <table class="table table-hover table-striped align-middle">
                                                    <thead class="custom-table-header">
                                                        <tr>
                                                            <th><i class="fa fa-user me-1"></i>Employee Name</th>
                                                            <th><i class="fa fa-money me-1"></i>Amount</th>
                                                            <th><i class="fa fa-info-circle me-1"></i>Status</th>
                                                            <th><i class="fa fa-calendar me-1"></i>Date</th>
                                                            <th><i class="fa fa-comment me-1"></i>Reason</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(req, idx) in manager.requests" :key="idx">
                                                            <td class="fw-semibold">{{ req.employeeName }}</td>
                                                            <td class="fw-bold" style="color: #3e5fce;">₹{{ formatAmount(req.amount) }}</td>
                                                            <td><span :class="getStatusClass(req.status)">{{ req.status }}</span></td>
                                                            <td>{{ formatDate(req.date) }}</td>
                                                            <td><small>{{ req.reason }}</small></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Site Report -->
                            <div v-if="reportData.reportType === 'site'">
                                <div class="report-header mb-4">
                                    <h5 style="color: #3e5fce;"><i class="fa fa-building-o me-2"></i>Site-wise Report</h5>
                                    <hr class="divider">
                                </div>
                                <div v-for="(site, index) in reportData.data" :key="index" class="card mb-4 shadow-sm border-0 report-card">
                                    <div class="card-body p-4">
                                        <div class="d-flex justify-content-between align-items-center mb-3">
                                            <h5 class="card-title text-dark mb-0">
                                                <i class="fa fa-map-marker me-2" style="color: #3e5fce;"></i>{{ site.siteName }}
                                            </h5>
                                            <button class="btn btn-sm px-3 custom-btn-outline" @click="toggleRequests(index)">
                                                <i :class="showRequests[index] ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                                                {{ showRequests[index] ? 'Hide' : 'Show' }} Details
                                            </button>
                                        </div>
                                        <div class="row g-3">
                                            <div class="col-md-6">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-users me-2"></i>Site Overview</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Requests:</span>
                                                        <span class="stat-value badge bg-primary">{{ site.totalRequests }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Employee Count:</span>
                                                        <span class="stat-value badge bg-info">{{ site.employeeCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Approved:</span>
                                                        <span class="stat-value badge bg-success">{{ site.approvedCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Rejected:</span>
                                                        <span class="stat-value badge bg-danger">{{ site.rejectedCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Pending:</span>
                                                        <span class="stat-value badge bg-warning text-dark">{{ site.pendingCount }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-6">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-rupee me-2"></i>Financial Summary</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Requested:</span>
                                                        <span class="stat-value text-primary fw-bold">₹{{ formatAmount(site.totalAmountRequested) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Approved:</span>
                                                        <span class="stat-value text-success fw-bold">₹{{ formatAmount(site.totalAmountApproved) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Rejected:</span>
                                                        <span class="stat-value text-danger fw-bold">₹{{ formatAmount(site.totalAmountRejected) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Highest Amount:</span>
                                                        <span class="stat-value text-success fw-bold">₹{{ formatAmount(site.highestAmount) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Lowest Amount:</span>
                                                        <span class="stat-value text-muted fw-bold">₹{{ formatAmount(site.lowestAmount) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div v-if="site.departments && site.departments.length > 0" class="mt-4">
                                            <h6 class="mb-3" style="color: #3e5fce;"><i class="fa fa-sitemap me-2"></i>Department Breakdown</h6>
                                            <div class="table-responsive">
                                                <table class="table table-hover table-sm align-middle">
                                                    <thead class="table-light">
                                                        <tr>
                                                            <th>Department</th>
                                                            <th class="text-center">Requests</th>
                                                            <th class="text-center">Approved</th>
                                                            <th class="text-center">Rejected</th>
                                                            <th class="text-end">Total Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(dept, idx) in site.departments" :key="idx">
                                                            <td class="fw-semibold">{{ dept.departmentName }}</td>
                                                            <td class="text-center"><span class="badge bg-primary">{{ dept.requestCount }}</span></td>
                                                            <td class="text-center"><span class="badge bg-success">{{ dept.approvedCount }}</span></td>
                                                            <td class="text-center"><span class="badge bg-danger">{{ dept.rejectedCount }}</span></td>
                                                            <td class="text-end text-primary fw-bold">₹{{ formatAmount(dept.totalAmount) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div v-if="showRequests[index]" class="mt-4">
                                            <h6 class="mb-3" style="color: #3e5fce;"><i class="fa fa-list me-2"></i>Detailed Requests</h6>
                                            <div class="table-responsive">
                                                <table class="table table-hover table-striped align-middle">
                                                    <thead class="custom-table-header">
                                                        <tr>
                                                            <th><i class="fa fa-user me-1"></i>Employee Name</th>
                                                            <th><i class="fa fa-sitemap me-1"></i>Department</th>
                                                            <th><i class="fa fa-money me-1"></i>Amount</th>
                                                            <th><i class="fa fa-info-circle me-1"></i>Status</th>
                                                            <th><i class="fa fa-calendar me-1"></i>Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(req, idx) in site.requests" :key="idx">
                                                            <td class="fw-semibold">{{ req.employeeName }}</td>
                                                            <td>{{ req.department }}</td>
                                                            <td class="fw-bold" style="color: #3e5fce;">₹{{ formatAmount(req.amount) }}</td>
                                                            <td><span :class="getStatusClass(req.status)">{{ req.status }}</span></td>
                                                            <td>{{ formatDate(req.date) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Employee Report -->
                            <div v-if="reportData.reportType === 'employee'">
                                <div class="report-header mb-4">
                                    <h5 style="color: #3e5fce;"><i class="fa fa-user me-2"></i>Employee-wise Report</h5>
                                    <hr class="divider">
                                </div>
                                <div v-for="(employee, index) in reportData.data" :key="index" class="card mb-4 shadow-sm border-0 report-card">
                                    <div class="card-body p-4">
                                        <div class="d-flex justify-content-between align-items-center mb-3">
                                            <h5 class="card-title text-dark mb-0">
                                                <i class="fa fa-id-badge me-2" style="color: #3e5fce;"></i>{{ employee.employeeName }}
                                            </h5>
                                            <button class="btn btn-sm px-3 custom-btn-outline" @click="toggleRequests(index)">
                                                <i :class="showRequests[index] ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                                                {{ showRequests[index] ? 'Hide' : 'Show' }} Details
                                            </button>
                                        </div>
                                        <div class="row g-3">
                                            <div class="col-md-3">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-info-circle me-2"></i>Employee Info</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label"><i class="fa fa-building-o me-1"></i>Site:</span>
                                                        <span class="stat-value fw-bold">{{ employee.site }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label"><i class="fa fa-sitemap me-1"></i>Department:</span>
                                                        <span class="stat-value fw-bold">{{ employee.department }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label"><i class="fa fa-briefcase me-1"></i>Designation:</span>
                                                        <span class="stat-value fw-bold">{{ employee.designation }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label"><i class="fa fa-money me-1"></i>Salary:</span>
                                                        <span class="stat-value text-success fw-bold">₹{{ formatAmount(employee.salary) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-file-text-o me-2"></i>Request Stats</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Requests:</span>
                                                        <span class="stat-value badge bg-primary">{{ employee.totalRequests }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Approved:</span>
                                                        <span class="stat-value badge bg-success">{{ employee.approvedCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Rejected:</span>
                                                        <span class="stat-value badge bg-danger">{{ employee.rejectedCount }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Pending:</span>
                                                        <span class="stat-value badge bg-warning text-dark">{{ employee.pendingCount }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-rupee me-2"></i>Amount Summary</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Requested:</span>
                                                        <span class="stat-value text-primary fw-bold">₹{{ formatAmount(employee.totalAmountRequested) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Approved:</span>
                                                        <span class="stat-value text-success fw-bold">₹{{ formatAmount(employee.totalAmountApproved) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Total Rejected:</span>
                                                        <span class="stat-value text-danger fw-bold">₹{{ formatAmount(employee.totalAmountRejected) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-3">
                                                <div class="stats-card bg-light p-3 rounded">
                                                    <h6 class="text-muted mb-3"><i class="fa fa-line-chart me-2"></i>Analytics</h6>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Rejection Rate:</span>
                                                        <span class="stat-value">
                                                            <div class="progress" style="height: 20px;">
                                                                <div class="progress-bar bg-danger" role="progressbar" 
                                                                     :style="{width: employee.rejectionRate + '%'}" 
                                                                     :aria-valuenow="employee.rejectionRate" 
                                                                     aria-valuemin="0" aria-valuemax="100">
                                                                    {{ employee.rejectionRate }}%
                                                                </div>
                                                            </div>
                                                        </span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Avg Amount:</span>
                                                        <span class="stat-value text-info fw-bold">₹{{ formatAmount(employee.averageRequestAmount) }}</span>
                                                    </div>
                                                    <div class="stat-item">
                                                        <span class="stat-label">Last Request:</span>
                                                        <span class="stat-value badge bg-secondary">{{ formatDate(employee.lastRequestDate) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div v-if="showRequests[index]" class="mt-4">
                                            <h6 class="mb-3" style="color: #3e5fce;"><i class="fa fa-list me-2"></i>Request History</h6>
                                            <div class="table-responsive">
                                                <table class="table table-hover table-striped align-middle">
                                                    <thead class="custom-table-header">
                                                        <tr>
                                                            <th><i class="fa fa-money me-1"></i>Amount</th>
                                                            <th><i class="fa fa-info-circle me-1"></i>Status</th>
                                                            <th><i class="fa fa-calendar me-1"></i>Date</th>
                                                            <th><i class="fa fa-comment me-1"></i>Reason</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(req, idx) in employee.requests" :key="idx">
                                                            <td class="fw-bold" style="color: #3e5fce;">₹{{ formatAmount(req.amount) }}</td>
                                                            <td><span :class="getStatusClass(req.status)">{{ req.status }}</span></td>
                                                            <td>{{ formatDate(req.date) }}</td>
                                                            <td><small>{{ req.reason }}</small></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- No Data State -->
                        <div v-else-if="!loading && reportData" class="text-center py-5">
                            <div class="empty-state">
                                <i class="fa fa-inbox fa-5x text-muted mb-3"></i>
                                <h5 class="text-muted">No Data Available</h5>
                                <p class="text-secondary">No records found for the selected filters. Try adjusting your search criteria.</p>
                            </div>
                        </div>

                        <!-- Initial State -->
                        <div v-else class="text-center py-5">
                            <div class="initial-state">
                                <i class="fa fa-bar-chart fa-5x mb-3" style="color: #3e5fce;"></i>
                                <h5 style="color: #3e5fce;">Ready to Generate Report</h5>
                                <p class="text-secondary">Select a report type and click "Generate Report" to view comprehensive analytics.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import axios from 'axios';
import * as XLSX from 'xlsx';

export default defineComponent({
    name: 'AdvanceSalaryReports',
    setup() {
        const loading = ref(false);
        const reportData = ref<any>(null);
        const error = ref<string | null>(null);
        const showRequests = ref<{ [key: number]: boolean }>({});

        const filters = ref({
            reportType: 'manager',
            startDate: '',
            endDate: '',
            managerId: '',
            siteId: '',
            departmentId: '',
            employeeId: '',
            status: ''
        });

        const managers = ref<any[]>([]);
        const sites = ref<any[]>([]);
        const departments = ref<any[]>([]);
        const employees = ref<any[]>([]);

        const fetchReport = async () => {
            if (!filters.value.reportType) {
                error.value = 'Please select a report type';
                return;
            }

            loading.value = true;
            error.value = null;
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                const params: any = {
                    reportType: filters.value.reportType
                };

                if (filters.value.startDate) params.startDate = filters.value.startDate;
                if (filters.value.endDate) params.endDate = filters.value.endDate;
                if (filters.value.managerId) params.managerId = filters.value.managerId;
                if (filters.value.siteId) params.siteId = filters.value.siteId;
                if (filters.value.departmentId) params.departmentId = filters.value.departmentId;
                if (filters.value.employeeId) params.employeeId = filters.value.employeeId;
                if (filters.value.status) params.status = filters.value.status;

                const response = await axios.get('http://localhost:5020/api/advance/salary/reports', {
                    params,
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                if (!response.data.success) {
                    throw new Error(response.data.message || 'Failed to fetch report');
                }

                reportData.value = response.data;
                
                // Show all requests by default
                showRequests.value = {};
                if (reportData.value && reportData.value.data) {
                    reportData.value.data.forEach((_: any, index: number) => {
                        showRequests.value[index] = true;
                    });
                }
            } catch (err: any) {
                console.error('Error fetching report:', err);
                error.value = err.response?.data?.message || err.message || 'Failed to fetch report';
            } finally {
                loading.value = false;
            }
        };

        const resetFilters = () => {
            filters.value = {
                reportType: 'manager',
                startDate: '',
                endDate: '',
                managerId: '',
                siteId: '',
                departmentId: '',
                employeeId: '',
                status: ''
            };
            reportData.value = null;
            showRequests.value = {};
            
            // Reset to default dates
            const today = new Date();
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            filters.value.startDate = firstDay.toISOString().split('T')[0];
            filters.value.endDate = today.toISOString().split('T')[0];
            
            // Reload manager report
            fetchReport();
        };

        const onReportTypeChange = () => {
            filters.value.managerId = '';
            filters.value.siteId = '';
            filters.value.employeeId = '';
        };

        const toggleRequests = (index: number) => {
            showRequests.value[index] = !showRequests.value[index];
        };

        const formatAmount = (amount: number) => {
            return amount ? amount.toLocaleString('en-IN') : '0';
        };

        const formatDate = (date: string) => {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('en-IN');
        };

        const getStatusClass = (status: string) => {
            const classes: { [key: string]: string } = {
                'Approved': 'badge bg-success',
                'Rejected': 'badge bg-danger',
                'Pending': 'badge bg-warning'
            };
            return classes[status] || 'badge bg-secondary';
        };

        const getCurrentDate = () => {
            const options: Intl.DateTimeFormatOptions = { 
                weekday: 'short', 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
            };
            return new Date().toLocaleDateString('en-US', options);
        };

        const exportToExcel = () => {
            if (!reportData.value || !reportData.value.data) return;

            const wb = XLSX.utils.book_new();
            const data = reportData.value.data;

            if (reportData.value.reportType === 'manager') {
                const wsData: any[] = [];
                data.forEach((manager: any) => {
                    wsData.push({
                        'Manager': manager.managerName,
                        'Total Requests': manager.totalRequests,
                        'Approved': manager.approvedCount,
                        'Rejected': manager.rejectedCount,
                        'Pending': manager.pendingCount,
                        'Total Amount Requested': manager.totalAmountRequested,
                        'Total Approved': manager.totalAmountApproved,
                        'Total Rejected': manager.totalAmountRejected,
                        'Critical Requests': manager.criticalRequestCount,
                        'Avg Request Amount': manager.averageRequestAmount,
                        'Approval Rate': manager.approvalRate + '%'
                    });
                });
                const ws = XLSX.utils.json_to_sheet(wsData);
                XLSX.utils.book_append_sheet(wb, ws, 'Manager Report');
            } else if (reportData.value.reportType === 'site') {
                const wsData: any[] = [];
                data.forEach((site: any) => {
                    wsData.push({
                        'Site': site.siteName,
                        'Total Requests': site.totalRequests,
                        'Employee Count': site.employeeCount,
                        'Approved': site.approvedCount,
                        'Rejected': site.rejectedCount,
                        'Pending': site.pendingCount,
                        'Total Amount Requested': site.totalAmountRequested,
                        'Total Approved': site.totalAmountApproved,
                        'Total Rejected': site.totalAmountRejected,
                        'Highest Amount': site.highestAmount,
                        'Lowest Amount': site.lowestAmount
                    });
                });
                const ws = XLSX.utils.json_to_sheet(wsData);
                XLSX.utils.book_append_sheet(wb, ws, 'Site Report');
            } else if (reportData.value.reportType === 'employee') {
                const wsData: any[] = [];
                data.forEach((employee: any) => {
                    wsData.push({
                        'Employee': employee.employeeName,
                        'Site': employee.site,
                        'Department': employee.department,
                        'Designation': employee.designation,
                        'Salary': employee.salary,
                        'Total Requests': employee.totalRequests,
                        'Approved': employee.approvedCount,
                        'Rejected': employee.rejectedCount,
                        'Pending': employee.pendingCount,
                        'Total Amount Requested': employee.totalAmountRequested,
                        'Total Approved': employee.totalAmountApproved,
                        'Total Rejected': employee.totalAmountRejected,
                        'Rejection Rate': employee.rejectionRate + '%',
                        'Avg Request Amount': employee.averageRequestAmount
                    });
                });
                const ws = XLSX.utils.json_to_sheet(wsData);
                XLSX.utils.book_append_sheet(wb, ws, 'Employee Report');
            }

            XLSX.writeFile(wb, `Advance_Salary_Report_${filters.value.reportType}_${new Date().toISOString().split('T')[0]}.xlsx`);
        };

        const fetchDropdownData = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = {
                    Authorization: `Bearer ${token}`
                };

                // Fetch managers, sites, departments, employees
                // Adjust these API endpoints according to your backend
                const [managersRes, sitesRes, departmentsRes, employeesRes] = await Promise.all([
                    axios.get('http://localhost:5020/api/employee/name-and-id?role=manager', { headers }).catch(() => ({ data: { data: [] } })),
                    axios.get('http://localhost:5020/api/sites/name-and-id', { headers }).catch(() => ({ data: { data: [] } })),
                    axios.get('http://localhost:5020/api/department/name-and-id', { headers }).catch(() => ({ data: { data: [] } })),
                    axios.get('http://localhost:5020/api/employee/name-and-id', { headers }).catch(() => ({ data: { data: [] } }))
                ]);

                managers.value = managersRes.data.data || [];
                sites.value = sitesRes.data.data || [];
                departments.value = departmentsRes.data.data || [];
                employees.value = employeesRes.data.data || [];
            } catch (error) {
                console.error('Error fetching dropdown data:', error);
            }
        };

        onMounted(async () => {
            await fetchDropdownData();
            
            // Set default dates (current month)
            const today = new Date();
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            filters.value.startDate = firstDay.toISOString().split('T')[0];
            filters.value.endDate = today.toISOString().split('T')[0];
            
            // Automatically load manager-wise report with all requests
            await fetchReport();
        });

        return {
            loading,
            reportData,
            filters,
            managers,
            sites,
            departments,
            employees,
            showRequests,
            fetchReport,
            resetFilters,
            onReportTypeChange,
            toggleRequests,
            formatAmount,
            formatDate,
            getStatusClass,
            exportToExcel,
            getCurrentDate
        };
    }
});
</script>

<style scoped>
/* Card Styling */
.card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border-radius: 12px;
    background-color: white;
}

.card.shadow-lg {
    box-shadow: 0 10px 30px rgba(62, 95, 206, 0.15);
}

.card.shadow-sm {
    box-shadow: 0 2px 8px rgba(62, 95, 206, 0.1);
}

.report-card {
    transition: transform 0.2s ease-in-out;
    background-color: white;
}

.report-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(62, 95, 206, 0.2) !important;
}

/* Header Styling */
.card-header {
    border-radius: 12px 12px 0 0;
}

.report-header h5 {
    font-weight: 600;
}

.divider {
    border: 0;
    height: 2px;
    background-color: #3e5fce;
    opacity: 0.3;
    margin-top: 0.5rem;
}

/* Filter Section */
.filter-section {
    background-color: white;
    border-radius: 12px;
}

.form-label {
    color: #495057;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
}

.form-select, .form-control {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-select:focus, .form-control:focus {
    border-color: #3e5fce;
    box-shadow: 0 0 0 0.2rem rgba(62, 95, 206, 0.25);
}

.form-select-lg {
    font-size: 1rem;
    padding: 0.6rem 1rem;
}

.form-control-lg {
    font-size: 1rem;
    padding: 0.6rem 1rem;
}

/* Action Buttons */
.action-buttons-section {
    border-top: 1px solid #e9ecef;
    padding-top: 1.5rem;
}

.btn {
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.btn-lg {
    padding: 0.6rem 1.5rem;
    font-size: 1rem;
}

.custom-btn-primary {
    background-color: #3e5fce;
    color: white;
    border: none;
}

.custom-btn-primary:hover:not(:disabled) {
    background-color: #2d4ab8;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(62, 95, 206, 0.4);
    color: white;
}

.custom-btn-primary:disabled {
    background-color: #a8b7e5;
    color: white;
}

.custom-btn-success {
    background-color: #28a745;
    color: white;
    border: none;
}

.custom-btn-success:hover:not(:disabled) {
    background-color: #218838;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(40, 167, 69, 0.4);
    color: white;
}

.custom-btn-success:disabled {
    background-color: #94d3a2;
    color: white;
}

.custom-btn-outline {
    border: 2px solid #3e5fce;
    color: #3e5fce;
    background-color: white;
}

.custom-btn-outline:hover {
    background-color: #3e5fce;
    color: white;
    transform: translateY(-1px);
}

/* Stats Cards */
.stats-card {
    background: white !important;
    border: 1px solid #e9ecef;
    transition: all 0.3s ease;
}

.stats-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(62, 95, 206, 0.15);
    border-color: #3e5fce;
}

.stats-card h6 {
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f1f3f5;
}

.stat-item:last-child {
    border-bottom: none;
}

.stat-label {
    font-size: 0.9rem;
    color: #6c757d;
}

.stat-value {
    font-size: 0.95rem;
    font-weight: 600;
}

/* Table Styling */
.table-responsive {
    border-radius: 8px;
    overflow: hidden;
}

.table {
    font-size: 0.9rem;
    margin-bottom: 0;
    background-color: white;
}

.table thead {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 0.5px;
}

.custom-table-header {
    background-color: #3e5fce;
    color: white;
}

.custom-table-header th {
    border: none;
    color: white;
    padding: 12px;
}

.table-light {
    background-color: #f8f9fa;
}

.table-hover tbody tr {
    transition: background-color 0.2s ease;
}

.table-hover tbody tr:hover {
    background-color: rgba(62, 95, 206, 0.05);
}

.table-striped tbody tr:nth-of-type(odd) {
    background-color: rgba(62, 95, 206, 0.02);
}

/* Badge Styling */
.badge {
    padding: 0.45em 0.8em;
    font-weight: 500;
    font-size: 0.85rem;
    border-radius: 6px;
}

.badge.bg-success {
    background-color: #28a745 !important;
    color: white;
}

.badge.bg-danger {
    background-color: #dc3545 !important;
    color: white;
}

.badge.bg-warning {
    background-color: #ffc107 !important;
    color: #000;
}

.badge.bg-primary {
    background-color: #3e5fce !important;
    color: white;
}

.badge.bg-info {
    background-color: #17a2b8 !important;
    color: white;
}

.badge.bg-secondary {
    background-color: #6c757d !important;
    color: white;
}

/* Progress Bar */
.progress {
    border-radius: 10px;
    background-color: #e9ecef;
}

.progress-bar {
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: width 0.6s ease;
}

/* Loading State */
.loading-container {
    padding: 3rem;
}

/* Empty States */
.empty-state, .initial-state {
    padding: 3rem 1rem;
}

.empty-state i, .initial-state i {
    opacity: 0.3;
}

/* Responsive Design */
@media (max-width: 768px) {
    .stats-card {
        margin-bottom: 1rem;
    }
    
    .col-md-3, .col-md-4, .col-md-6 {
        margin-bottom: 1rem;
    }
    
    .action-buttons-section .btn {
        width: 100%;
        margin-bottom: 0.5rem;
    }
    
    .table {
        font-size: 0.8rem;
    }
}

/* Animations */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.report-card {
    animation: fadeIn 0.5s ease-in-out;
}

/* Custom Scrollbar */
.table-responsive::-webkit-scrollbar {
    height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: #555;
}
</style>
